import React from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import { addQuery, QueryVal } from 'Helpers/Query';
import MetaTable from 'Components/UI/Meta/MetaTable';
import useUserContext from 'Context/UserContext';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import {
  displaySuccess,
  displayErrorMessage,
} from 'Helpers/FacetNotificationManager';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { advisorHouseholdUri } from 'Consts/advisorUris';
import Button from 'Components/UI/Button';
import SaveQueryButton from 'Components/UI/SaveQueryButton';
import {
  getActionItemFilters,
} from 'Overrides/ActionItem';
import modalConsts from 'Consts/ModalNames';
import { pickFunction } from 'Helpers/Affordances';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { DataValueMap } from 'Helpers/Testing';
import { balanceSheetPageURL } from 'Hooks/useBalanceSheetPageURL';
import { navigateToUriInNewTab } from 'Helpers/Navigation';
import LoadingIndicator from './LoadingIndicator';

const styles = theme => ({
  root: {
    width: '100%',
  },
  filtersRow: {
    width: '100%',
    display: 'flex',
  },
  filterSelectContainer: {
    width: theme.dimensions.md,
    margin: theme.spacing(5),
  },
  tableFilterContainer: {
    marginTop: '-1',
    marginLeft: '-1',
  },
  content: {
    width: '100%',
  },
  addButtonIcon: {
    marginRight: theme.spacing(1),
  },
  addButtonBox: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

function SimpleActionItemTable({ className, classes }) {
  const { householdSearch } = useSimpleHousehold();
  const { agentResource, userResource, isAgent } = useUserContext();
  const { rootNodeProps } = useEventTrackingLogEvent({
    nodeName: 'Task Board',
    isPageView: true,
  });

  let collectionLink = QueryVal('link');
  if (!collectionLink) {
    collectionLink = agentResource
      ?.relationship('filteredActionItems')
      .link('related')
      .url({ params: { limit: 20 } });
    addQuery({
      link: agentResource
        ?.relationship('filteredActionItems')
        .link('related')
        .url({ params: { limit: 20 } }),
    });
  }

  if (!collectionLink) {
    return <LoadingIndicator />;
  }

  const dialogRows = [
    ['name'],
    ['dueDate', 'priority'],
    ['status', 'timeToComplete'],
    ['assignedTo', 'showOnBlueprint'],
    ['pillar'],
    ['description'],
  ];

  const onSubmit = () => {
    displaySuccess('Todo Saved');
  };

  const onDelete = () => {
    displaySuccess('Todo Deleted');
  };

  const onFailure = err => {
    displayErrorMessage(err, err.errors);
  };

  const metaTable = (
    <MetaTable
      classes={{
        root: classNames(classes.root, className),
        filterContainer: classes.tableFilterContainer,
      }}
      collection={collectionLink}
      refreshNowLink="refreshSimpleActionItems"
      tableMetaOverrideSetName="SimpleActionItemTableOverride"
      hasMetaQueryForm
      hasSortableColumns
      QueryFormProps={{
        square: true,
        getAvailableFilters: collection => {
          const availableFilters = pickFunction(userResource, {
            [fr.relationships.default.name]: () => {
              return getActionItemFilters(collection, householdSearch);
            },
          });

          return availableFilters;
        },
        AdditionalActions: (
          <>
            <SaveQueryButton link={QueryVal('link')} queryName="task-board" />
          </>
        ),
      }}
      features={{
        edit: {
          type: 'hybrid',
          metaOverrideSetName: 'SimpleActionItemEditOverride',
          title: resource => `Edit Task for ${resource.attr('clientName')}`,
          rows: dialogRows,
          refetchResource: true,
          additionalActions: [
            {
              component: resource => {
                return (
                  <Link
                    to={advisorHouseholdUri(resource.attr('householdId'))}
                    target="_blank"
                  >
                    <Button color="secondary" variant="contained">
                            View Client Page
                    </Button>
                  </Link>
                );
              },
            },
            {
              component: resource => {
                const url = balanceSheetPageURL(resource, resource.attr('householdId'), isAgent);
                if (!url) {
                  return null;
                }
                return (
                  <Button
                    color="secondary"
                    variant="contained"
                    onClick={(e) => navigateToUriInNewTab(e, url)}
                  >
                    Edit Account
                  </Button>
                );
              },
            },
          ],
          onSubmit,
          onFailure,
        },
        delete: {
          type: 'dialog',
          onSubmit: onDelete,
          onFailure,
        },
      }}
      TableContainerProps={{
        ...rootNodeProps,
      }}
    />
  );

  return (
    <div className={classes.content} {...DataValueMap.init('simple-action-item-table').build()}>
      <div className={classes.addButtonBox}>
        <Button
          color="secondary"
          className={classes.addButton}
          onClick={() => addQuery({ modal: modalConsts.simpleActionItemGroupModal })}
        >
          <AddCircleIcon className={classes.addButtonIcon} />
          {'Add Tasks'}
        </Button>
      </div>
      {metaTable}
    </div>
  );
}

SimpleActionItemTable.defaultProps = {
  className: '',
};

SimpleActionItemTable.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(SimpleActionItemTable);
